import Big from 'big.js';
import StatusChip from 'components/@extended/StatusChip';
import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import { getLabelCardColorByNumber, LabelCard } from 'components/cards/LabelCard';
import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import InfoTooltip from 'components/InfoTooltip';
import PartyName from 'components/party/PartyName';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { getLinkChips, getLinks } from 'pages/matters/util';
import { useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useDeletePartyGrouppartyHistoryEntryMutation,
    useGetPartyGroupPartyHistoryQuery,
    useUpdatePartyGroupPartyHistoryEntryMutation,
} from 'store/api/groups';
import { ClassifierData } from 'types/classifiers';
import { GroupEmployeeRoleType, GroupMemberRoleType, PartyGroup } from 'types/groups';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import { OfficeMin } from 'types/offices';
import { TeamMinDto } from 'types/teams';
import { useLastEffortType } from 'hooks/useLastEffortType';
import { getClassifierText, t } from 'utils/getTranslations';
import { sortByField } from 'utils/sort';
import { calculateTimeSince } from '../../contacts/util.tsx';
import { GroupSection } from '../form-sections/GroupSection';
import { GroupSectionId, groupDtoToForm } from '../groupFormMapping';

// Over InfoTooltip's `h-5 w-auto`: a 1rem-tall glyph in its own 1em-wide box.
const INFO_ICON_CLASS = 'text-grey-600 h-[1rem] w-[1em]';

interface Props {
    classifiers: Record<string, ClassifierData>;
    partyGroup: PartyGroup;
    officeList: OfficeMin[] | undefined;
    teamList: TeamMinDto[] | undefined;
    /** The new design: a card per topic, each editing its own fields in place, beside the figures. */
    isNew?: boolean;
}

const GeneralView: React.FC<Props> = ({ partyGroup, classifiers, officeList, teamList, isNew = false }) => {
    const { revenueStatistics, relatedMatterCount } = partyGroup;
    const dateFormat = useEffectiveDateFormat();
    const timeSinceLastEffort = calculateTimeSince(partyGroup.lastEffortDate, null, dateFormat).text;
    const lastEffortType = useLastEffortType();
    const revenuePercentage: number = revenueStatistics?.revenueYtdM1
        ? Math.round(
              (((revenueStatistics.revenueYtd ?? 0) - revenueStatistics.revenueYtdM1) /
                  revenueStatistics.revenueYtdM1) *
                  100
          )
        : 0;
    const averageRatePercentage: number = revenueStatistics?.averageRateFyM1
        ? Math.round(
              (((revenueStatistics.averageRateFy ?? 0) - revenueStatistics.averageRateFyM1) /
                  revenueStatistics.averageRateFyM1) *
                  100
          )
        : 0;

    const renderBusinessAreas = useCallback((): React.ReactNode => {
        const areaTypeCodes = partyGroup?.businessAreas
            ? (partyGroup.businessAreas || []).map((item) => item.businessAreaTypeCode)
            : ((partyGroup.operatesInBusinessArea || []).map((item) => item.businessAreaCode) ?? []);
        if (areaTypeCodes.length === 0) {
            return '-';
        }
        return areaTypeCodes.map((areaTypeCode, index) => {
            const optionalComma = index < areaTypeCodes.length - 1 ? `,\u00A0` : null;
            return (
                <div key={areaTypeCode}>
                    {getClassifierText(classifiers['BUSINESS_AREA_TYPE'], areaTypeCode)}
                    {optionalComma}
                </div>
            );
        });
    }, [partyGroup, classifiers]);

    const officeListSorted = useMemo(() => {
        return (officeList || [])
            .filter((office) => office.isActive !== false)
            .toSorted((office) =>
                partyGroup?.servedByOffices?.some((servedOffice) => servedOffice.id === office.id) ? -1 : 0
            );
    }, [officeList, partyGroup.servedByOffices]);

    const practiceGroupListSorted = useMemo(() => {
        return (teamList || []).toSorted((pg) =>
            partyGroup?.servedByPracticeGroups?.some((servedPg) => servedPg.id === pg.id) ? -1 : 0
        );
    }, [teamList, partyGroup.servedByPracticeGroups]);

    const getGeneralData = useCallback(
        (): Row[] => [
            {
                content: (
                    <div>
                        {partyGroup.parties
                            ? getLinks(
                                  'contact',
                                  partyGroup.parties
                                      .filter((item) => item.groupMemberRoleTypeCode === GroupMemberRoleType.MEMBER)
                                      .map((item) => item.party),
                                  { renderMethod: 'vertical' }
                              )
                            : '-'}
                    </div>
                ),
                label: (
                    <div className="flex gap-[.5ch]">
                        <FormattedMessage id="groups.group-members" />
                        <DisplayHistoryButton
                            entityType={HistoryEntityType.PARTY_GROUP}
                            entityId={`${partyGroup.id}`}
                            fieldType={HistoryFieldType.MEMBER}
                            fieldName={t('groups.group-members')}
                            activity=""
                            query={useGetPartyGroupPartyHistoryQuery}
                            updateMutation={useUpdatePartyGroupPartyHistoryEntryMutation}
                            deleteMutation={useDeletePartyGrouppartyHistoryEntryMutation}
                        />
                    </div>
                ),
            },
            {
                content: partyGroup.parties
                    ? getLinks(
                          'contact',
                          partyGroup.parties
                              .filter((item) => item.groupMemberRoleTypeCode === GroupMemberRoleType.PRIMARY_CONTACT)
                              .map((item) => item.party)
                      )
                    : '-',
                label: t('groups.group-primary-contacts'),
            },
            {
                content: partyGroup.employees
                    ? getLinks(
                          'employee',
                          partyGroup.employees
                              .filter(
                                  (item) =>
                                      item.groupEmployeeRoleTypeCode === GroupEmployeeRoleType.RELATIONSHIP_PARTNER
                              )
                              .map((item) => item.employee)
                      )
                    : '-',
                label: t('groups.group-relationship-partners'),
            },
            {
                content: partyGroup.employees
                    ? getLinks(
                          'employee',
                          partyGroup.employees
                              .filter(
                                  (item) =>
                                      item.groupEmployeeRoleTypeCode === GroupEmployeeRoleType.RELATIONSHIP_MANAGER
                              )
                              .map((item) => item.employee)
                      )
                    : '-',
                label: t('groups.group-relationship-managers'),
            },
            {
                content:
                    getClassifierText(classifiers['GROUP_PARTNERSHIP_TYPE'], partyGroup.groupPartnershipTypeCode) ||
                    '-',
                label: t('groups.partnership-type'),
            },
            {
                label: (
                    <div className="flex gap-[.5ch]">
                        <FormattedMessage id="groups.segment-type" />
                        <DisplayHistoryButton
                            entityType={HistoryEntityType.PARTY_GROUP}
                            entityId={`${partyGroup.id}`}
                            fieldType={HistoryFieldType.SEGMENT_TYPE_CODE}
                            fieldName={t('groups.segment-type')}
                            activity={t('updating')}
                        />
                    </div>
                ),
                content: (
                    <span className="text-body1">
                        {partyGroup.segmentTypeCode
                            ? getClassifierText(classifiers['SEGMENT_TYPE'], partyGroup.segmentTypeCode)
                            : '-'}
                    </span>
                ),
            },
            {
                label: (
                    <div className="flex gap-[.5ch]">
                        <FormattedMessage id="groups.service-level-type" />
                        <DisplayHistoryButton
                            entityType={HistoryEntityType.PARTY_GROUP}
                            entityId={`${partyGroup.id}`}
                            fieldType={HistoryFieldType.SERVICE_LEVEL_TYPE_CODE}
                            fieldName={t('groups.service-level-type')}
                            activity={t('updating')}
                        />
                    </div>
                ),
                content: (
                    <span className="text-body1">
                        {partyGroup.serviceLevelTypeCode
                            ? getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], partyGroup.serviceLevelTypeCode)
                            : '-'}
                    </span>
                ),
            },
            {
                content: partyGroup.groupTypeCode
                    ? getClassifierText(classifiers['GROUP_TYPE'], partyGroup.groupTypeCode) || '-'
                    : '-',
                label: t('groups.group-type'),
            },
        ],
        [partyGroup, classifiers]
    );

    const getOverviewData = useCallback(
        (): Row[] => [
            {
                label: t('groups.operates-in-business-areas'),
                content: renderBusinessAreas(),
            },
            {
                label: (
                    <span className="flex items-center gap-1">
                        {t('groups.served-by-offices')}
                        <InfoTooltip text="contacts.tooltips.served-by-offices-detail" className={INFO_ICON_CLASS} />
                    </span>
                ),
                content: (
                    <span className="flex flex-wrap gap-1">
                        {officeListSorted?.map((office) => (
                            <StatusChip
                                key={office.id}
                                label={office.abbreviation}
                                type={
                                    partyGroup?.servedByOffices?.some((servedOffice) => servedOffice.id === office.id)
                                        ? 'neutral'
                                        : 'inactive'
                                }
                            />
                        )) || '-'}
                    </span>
                ),
            },
            {
                label: (
                    <span className="flex items-center gap-1">
                        {t('groups.served-by-practice-groups')}
                        <InfoTooltip
                            text="contacts.tooltips.served-by-practice-groups-detail"
                            className={INFO_ICON_CLASS}
                        />
                    </span>
                ),
                content: (
                    <span className="flex flex-wrap gap-1">
                        {practiceGroupListSorted?.length
                            ? practiceGroupListSorted?.map((pg) => (
                                  <StatusChip
                                      key={pg.id}
                                      label={pg.abbreviation}
                                      type={
                                          partyGroup?.servedByPracticeGroups?.some(
                                              (servedPracticeGroup) => servedPracticeGroup.id === pg.id
                                          )
                                              ? 'neutral'
                                              : 'inactive'
                                      }
                                  />
                              ))
                            : '-'}
                    </span>
                ),
            },
            {
                content: partyGroup.notes || '-',
                label: t('groups.notes'),
                multiline: true,
            },
        ],
        [officeListSorted, partyGroup, practiceGroupListSorted, renderBusinessAreas]
    );

    const labelCardsSequence = (
        <div className="new:gap-(--card-gap) flex flex-col gap-4">
            <LabelCard labelId="contacts.last-bd-effort" content={timeSinceLastEffort} />
            {lastEffortType && (
                <LabelCard
                    label={lastEffortType.label}
                    content={calculateTimeSince(partyGroup.lastHCSEffortDate, null, dateFormat).text}
                />
            )}
            <LabelCard
                labelId="contacts.invoiced-revenue-ytd"
                tooltipText="contacts.tooltips.invoiced-revenue-ytd"
                fontSize="large"
                content={
                    <>
                        <MoneyDisplay
                            amount={revenueStatistics?.revenueYtd ?? 0}
                            currency={revenueStatistics?.baseCurrencyTypeCode}
                            decimalScale={0}
                        />
                        {revenueStatistics?.revenueYtdM1 ? (
                            <div className="text-body1 font-normal">
                                {`${revenuePercentage > 0 ? '+' : ''}${revenuePercentage}% vs. LYTD`}
                            </div>
                        ) : null}
                    </>
                }
                color={getLabelCardColorByNumber(revenuePercentage)}
            />
            <LabelCard
                labelId="contacts.invoiced-revenue-3y"
                tooltipText="contacts.tooltips.invoiced-revenue-3y"
                fontSize="large"
                content={
                    <MoneyDisplay
                        amount={revenueStatistics?.revenueTotalLastQuarter3y ?? 0}
                        currency={revenueStatistics?.baseCurrencyTypeCode}
                        decimalScale={0}
                    />
                }
            />
            <LabelCard
                labelId="contacts.case-count-3y"
                tooltipText="groups.tooltips.case-count-3y"
                content={relatedMatterCount ?? 0}
                fontSize="large"
            />
            <LabelCard
                labelId="contacts.average-rate-ytd"
                tooltipText="contacts.tooltips.average-rate-ytd"
                fontSize="large"
                content={
                    <>
                        <MoneyDisplay
                            amount={revenueStatistics?.averageRateFy ?? 0}
                            currency={revenueStatistics?.baseCurrencyTypeCode}
                        />
                        {revenueStatistics?.averageRateFyM1 ? (
                            <div className="text-body1 font-normal">
                                {`vs. ${revenueStatistics?.averageRateFyM1 ?? '0.00'} LY (${averageRatePercentage > 0 ? '+' : ''}${new Big(averageRatePercentage).toFixed(2)}%)`}
                            </div>
                        ) : null}
                    </>
                }
                color={getLabelCardColorByNumber(averageRatePercentage)}
            />
        </div>
    );

    // On a draft, the values it was saved without read as missing, and whom it is shared with stands in for the
    // figures, which come from cases a draft has none of.
    const draftActions = useContext(DraftActionsContext);
    const missingFields = draftActions?.draft?.missingFields;
    const figures = draftActions ? <DraftSharedWith /> : labelCardsSequence;

    if (isNew) {
        const [members, primaryContacts, partners, managers, partnershipType, segment, serviceLevel, groupType] =
            getGeneralData();
        const [businessAreas, servedByOffices, servedByPracticeGroups, notes] = getOverviewData();
        const card = (id: GroupSectionId, titleId: string, rows: Row[]) => (
            <GroupSection id={id} titleId={titleId} partyGroup={partyGroup} rows={rows}>
                {(edit) => (
                    <GridItemList isNew titleId={titleId} data={withMissing(rows, missingFields)} button={edit} />
                )}
            </GroupSection>
        );
        // A card lists the group form's fields for it in the form's order, each drawn as its field: a multi-select's
        // values as chips, sorted by name as the form sorts them, each still a link to its record.
        const partyChips = (role: GroupMemberRoleType) =>
            sortByField(
                (partyGroup.parties ?? [])
                    .filter((item) => item.groupMemberRoleTypeCode === role)
                    .map((item) => item.party),
                'name'
            ).map((party) => ({
                key: party.id,
                label: <PartyName party={party} isLink variant="readonly-other" singleLine />,
            }));
        const employeeChips = (role: GroupEmployeeRoleType) =>
            getLinkChips(
                'employee',
                sortByField(
                    (partyGroup.employees ?? [])
                        .filter((item) => item.groupEmployeeRoleTypeCode === role)
                        .map((item) => item.employee),
                    'name'
                )
            );
        const classifierText = (classifier: string, code: string | null | undefined) =>
            (code && getClassifierText(classifiers[classifier], code)) || '-';
        // The cards on the left, each edited on its own, and the figures on the right, as on a contact.
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {card('details', 'groups.details', [
                        { label: t('groups.name'), content: partyGroup.groupName || '-' },
                        // Its radio options take a row of their own, whatever is picked.
                        { ...groupType, label: t('groups.type'), wide: 'always' },
                        partnershipType,
                        // The option's text alone, on the line a select has it on.
                        { ...segment, content: classifierText('SEGMENT_TYPE', partyGroup.segmentTypeCode) },
                        {
                            ...serviceLevel,
                            content: classifierText('SERVICE_LEVEL_TYPE', partyGroup.serviceLevelTypeCode),
                        },
                    ])}
                    {card('members', 'groups.members-and-relationship', [
                        {
                            ...businessAreas,
                            label: infoLabel(
                                'groups.operates-in-business-areas',
                                'groups.tooltips.operates-in-business-areas',
                                'justify-between'
                            ),
                            chips: groupDtoToForm(partyGroup, classifiers).businessAreas.map(({ label, value }) => ({
                                key: value,
                                label,
                            })),
                        },
                        { ...members, content: '-', chips: partyChips(GroupMemberRoleType.MEMBER) },
                        {
                            ...primaryContacts,
                            content: '-',
                            chips: partyChips(GroupMemberRoleType.PRIMARY_CONTACT),
                        },
                        {
                            ...partners,
                            content: '-',
                            chips: employeeChips(GroupEmployeeRoleType.RELATIONSHIP_PARTNER),
                        },
                        {
                            ...managers,
                            content: '-',
                            chips: employeeChips(GroupEmployeeRoleType.RELATIONSHIP_MANAGER),
                        },
                        // Derived from the group's cases: no field edits them.
                        { ...servedByOffices, wide: true, readOnly: true },
                        { ...servedByPracticeGroups, wide: true, readOnly: true },
                    ])}
                    {card('notes', 'groups.notes', [notes])}
                </div>
                {figures}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-12">
            {draftActions ? null : <div className="sm:col-span-2">{labelCardsSequence}</div>}
            <div className={draftActions ? 'sm:col-span-6' : 'sm:col-span-5'}>
                <GridItemList
                    titleId="groups.details"
                    data={withMissing(getGeneralData(), missingFields)}
                    key="group-general"
                />
            </div>
            <div className={draftActions ? 'flex flex-col gap-6 sm:col-span-6' : 'sm:col-span-5'}>
                {draftActions ? <DraftSharedWith /> : null}
                <GridItemList
                    titleId="groups.overview"
                    data={withMissing(getOverviewData(), missingFields)}
                    key="group-financial"
                />
            </div>
        </div>
    );
};

export default GeneralView;
